import { useTranslation } from 'react-i18next';
import {
	Typography,
	Card,
	CardContent,
	Box,
	Chip,
	IconButton,
} from '@mui/material';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import {
	useGet21DetailListQuery,
	useGetUserDetailQuery,
	useUnlockAdditionalMutation,
	useUnlockDayMutation,
} from '@/api/user/userApiSlice';
import { Series21UnlockListResDTO } from '@/api/DTO/user/userList.interface';
import LocalDataGrid from '@/common/components/dataGrid/LocalDataGrid';
import { useGetDay21DetailQuery } from '@/api/exercise/day21ApiSlice';
import { showLabel } from '@/utils/helper';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import CloseIcon from '@mui/icons-material/Close';
import { closeSnackbar, enqueueSnackbar } from 'notistack';

export interface DeleteUserListDataGrid {
	dayLastFinishTime: string;
	additionalLastFinishTime: string;
}

export default function UnlockMajorSummaryPage() {
	const { t } = useTranslation();
	const { id, seriesId } = useParams();
	const showModal = useModal();
	const [unlockDay] = useUnlockDayMutation();
	const [unlockAdditional] = useUnlockAdditionalMutation();

	const lockList = [
		{ id: 0, name: t('user.unlock') },
		{ id: 1, name: t('user.finish') },
		{ id: 2, name: t('user.lock') },
	];

	const { data: userDetail }: any = useGetUserDetailQuery(id || '', {
		refetchOnMountOrArgChange: true,
	});

	const { data: details } = useGetDay21DetailQuery(seriesId || '', {
		refetchOnMountOrArgChange: true,
	});

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGet21DetailListQuery(
			{ userId: id || '', seriesId: seriesId || '' },
			{
				refetchOnMountOrArgChange: true,
			}
		);

	const transformResponse = useCallback(
		(response?: Series21UnlockListResDTO[]) => {
			if (response == null) return;
			const newResponse: DeleteUserListDataGrid[] = [];
			response.forEach((item) => {
				newResponse.push({
					...item,
					dayLastFinishTime: !item.dayLastFinishTime
						? '-'
						: dayjs(item.dayLastFinishTime)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
					additionalLastFinishTime: !item.additionalLastFinishTime
						? '-'
						: dayjs(item.additionalLastFinishTime)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
				});
			});
			return newResponse;
		},
		[]
	);

	const handleUnlock = (row: Series21UnlockListResDTO) => {
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={true}
				title={
					row.dayStatus == 2
						? t('user.unlockExercise')
						: t('user.unlockAdditionalExercise')
				}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>
						{row.dayStatus == 2
							? t('user.isUnlockExercise', { day: row.day })
							: t('user.isUnlockAdditionalExercise')}
					</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.isConfirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response =
						row.dayStatus == 2
							? await unlockDay({ userId: id || '', dayId: row.dayId })
							: await unlockAdditional({
									userId: id || '',
									additionalId: row.additionalId || '',
							  });
					if ('data' in response) {
						enqueueSnackbar(
							row.dayStatus == 2
								? t('snackbar.unlockExerciseSuccess')
								: t('snackbar.unlockAdditionalExerciseSuccess'),
							{
								variant: 'success',
								anchorOrigin: { horizontal: 'center', vertical: 'top' },
								autoHideDuration: 3000,
								action: (key) => {
									return <CloseIcon onClick={() => closeSnackbar(key)} />;
								},
							}
						);
					}
				}}
			/>
		));
	};

	const columns: GridColDef[] = [
		{
			field: 'day',
			headerName: t('day21.exerciseDay'),
			flex: 21,
		},
		{
			field: 'titleZh',
			headerName: t('day21.exerciseTitleZh'),
			flex: 21,
		},
		{
			field: 'dayStatus',
			headerName: t('user.userExerciseStatus'),
			flex: 21,
			renderCell: (params) => (
				<Chip
					color={
						params.row.dayStatus == 1
							? 'chipPrimary'
							: params.row.dayStatus == 2
							? 'default'
							: 'chipTertiary'
					}
					label={showLabel(params.row.dayStatus, lockList)}
				/>
			),
		},
		{
			field: 'additionalStatus',
			headerName: t('user.additionalStatus'),
			flex: 21,
			renderCell: (params) =>
				typeof params.row.additionalStatus == 'number' ? (
					<Chip
						color={
							params.row.additionalStatus == 1
								? 'chipPrimary'
								: params.row.additionalStatus == 2
								? 'default'
								: 'chipTertiary'
						}
						label={showLabel(params.row.additionalStatus, lockList)}
					/>
				) : (
					'-'
				),
		},
		{
			field: 'dayLastFinishTime',
			headerName: t('user.finalCompletionTime'),
			flex: 21,
		},
		{
			field: 'additionalLastFinishTime',
			headerName: t('user.additionalCompletionTime'),
			flex: 21,
		},
		{
			field: 'edit',
			headerName: '',
			width: 68,
			renderCell: (params) =>
				(params.row.dayStatus == 2 || params.row.additionalStatus == 2) && (
					<IconButton color='primary'>
						<LockOpenIcon onClick={() => handleUnlock(params.row)} />
					</IconButton>
				),
		},
	];

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h4'>
					{details?.data.titleZh}({userDetail?.data.username},
					{userDetail?.data.nickname})
				</Typography>
			</Box>

			<Card sx={{ marginTop: 3 }}>
				<CardContent>
					<Typography variant='h6'>{t('day21.exerciseList')}</Typography>
					<Box marginTop={2}>
						<LocalDataGrid
							rows={transformResponse(propertySummary?.data) ?? []}
							columns={columns}
							rowCount={0}
							getRowId={(row) => row.dayId}
							pagination={false}
						/>
					</Box>
				</CardContent>
			</Card>
		</>
	);
}
